import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { InlineTextField } from "@/components/inline-edit/inline-text-field";
import { InlineSelectField } from "@/components/inline-edit/inline-select-field";
import { InlineDateField } from "@/components/inline-edit/inline-date-field";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import type { Facility, Deal, Lender } from "@shared/schema";

const STATUS_OPTIONS = [
  { value: "indicative", label: "Indicative" },
  { value: "submitted", label: "Submitted" },
  { value: "approved", label: "Approved" },
  { value: "settled", label: "Settled" },
  { value: "declined", label: "Declined" },
  { value: "withdrawn", label: "Withdrawn" },
  { value: "scenario_not_proceeding", label: "Scenario Not Proceeding" },
];

const FACILITY_TYPE_OPTIONS = [
  { value: "term_loan", label: "Term Loan" },
  { value: "overdraft", label: "Overdraft" },
  { value: "equipment_finance", label: "Equipment Finance" },
  { value: "trade_finance", label: "Trade Finance" },
  { value: "invoice_finance", label: "Invoice Finance" },
  { value: "property_loan", label: "Property Loan" },
  { value: "construction", label: "Construction" },
  { value: "bridging", label: "Bridging" },
  { value: "other", label: "Other" },
];

function formatCurrency(value: string | null | undefined): string {
  if (!value) return "-";
  const num = parseFloat(value);
  if (isNaN(num)) return "-";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function formatDate(value: string | Date | null | undefined): string {
  if (!value) return "-";
  const date = typeof value === "string" ? new Date(value) : value;
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

interface FacilityOverviewTabProps {
  facility: Facility;
  deal?: Deal | null;
  dealId: string;
}

export function FacilityOverviewTab({ facility, deal, dealId }: FacilityOverviewTabProps) {
  const { toast } = useToast();

  // ISSUE-119 Rework #1: State for settled confirmation dialog
  const [showSettledConfirm, setShowSettledConfirm] = useState(false);
  const [settledAmountInput, setSettledAmountInput] = useState<string>("");
  const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);

  // Fetch lenders for dropdown
  const { data: lenders = [] } = useQuery<Lender[]>({
    queryKey: ["/api/crm/lenders", { status: "active" }],
  });

  // Fetch parent facility name when this facility is a variant (F020)
  const { data: parentFacility } = useQuery<Facility>({
    queryKey: ["/api/crm/facilities", facility.parentFacilityId],
    enabled: !!facility.parentFacilityId,
  });

  // Convert lenders to select options format
  // Note: Using "__none__" instead of "" because Radix Select doesn't allow empty string values
  const lenderOptions = [
    { value: "__none__", label: "No lender selected" },
    ...lenders.map((lender) => ({
      value: lender.id,
      label: lender.lenderName,
    })),
  ];

  const updateMutation = useMutation({
    mutationFn: async (updates: Partial<Facility>) => {
      const response = await apiRequest("PATCH", `/api/crm/facilities/${facility.id}`, updates);
      return response;
    },
    onMutate: async (updates) => {
      await queryClient.cancelQueries({ queryKey: ["/api/crm/facilities", facility.id] });
      const previousFacility = queryClient.getQueryData(["/api/crm/facilities", facility.id]);
      if (previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", facility.id], {
          ...previousFacility,
          ...updates,
        });
      }
      return { previousFacility };
    },
    onError: (error: Error, _variables, context) => {
      if (context?.previousFacility) {
        queryClient.setQueryData(["/api/crm/facilities", facility.id], context.previousFacility);
      }
      toast({
        title: "Error",
        description: error.message || "Failed to save changes",
        variant: "destructive",
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities"] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/facilities", facility.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId, "facilities"] });
      // Invalidate deal queries so Financials tab refreshes (deal.amountSettled updates via BR-002 trigger)
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals", dealId] });
      queryClient.invalidateQueries({ queryKey: ["/api/crm/deals"] });
    },
  });

  // ISSUE-119 Rework #1: Intercept facility status changes to "settled"
  const handleFieldUpdate = async (field: keyof Facility, value: any): Promise<void> => {
    // Intercept facility status changes to "settled"
    if (field === "facilityStatus" && value === "settled" && facility.facilityStatus !== "settled") {
      // Trigger confirmation dialog
      setPendingStatusChange(value);
      setSettledAmountInput(facility.approvedLimit || "");
      setShowSettledConfirm(true);
      return; // Don't save yet
    }

    // For all other updates, save directly
    await updateMutation.mutateAsync({ [field]: value });
  };

  // ISSUE-119 Rework #1: Handler for settled confirmation dialog Cancel
  const handleSettledCancel = () => {
    setPendingStatusChange(null);
    setShowSettledConfirm(false);
    setSettledAmountInput("");
  };

  // ISSUE-119 Rework #1: Handler for settled confirmation dialog Confirm
  const handleSettledConfirm = async () => {
    const amount = parseFloat(settledAmountInput);

    if (!amount || amount <= 0) {
      toast({
        title: "Invalid Amount",
        description: "Settled Amount must be greater than zero.",
        variant: "destructive",
      });
      return;
    }

    // Save BOTH status and fundedAmount
    await updateMutation.mutateAsync({
      facilityStatus: pendingStatusChange as "submitted" | "settled" | "indicative" | "approved" | "declined" | "withdrawn" | "scenario_not_proceeding" | null,
      fundedAmount: settledAmountInput,
    });

    setShowSettledConfirm(false);
    setPendingStatusChange(null);
    setSettledAmountInput("");

    toast({
      title: "Settled Amount Confirmed",
      description: `Settled Amount set to ${formatCurrency(settledAmountInput)}.`,
    });
  };

  return (
    <div className="space-y-3">
      <Card>
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">FACILITY DETAILS</h3>
          <div className="grid grid-cols-2 gap-3">
            <InlineTextField
              value={facility.facilityName}
              onSave={(value) => handleFieldUpdate("facilityName", value)}
              label="Facility Name"
              placeholder="Enter facility name"
              testId="input-facility-name"
            />
            <InlineSelectField
              value={facility.facilityType || ""}
              options={FACILITY_TYPE_OPTIONS}
              onSave={(value) => handleFieldUpdate("facilityType", value || null)}
              label="Facility Type"
              placeholder="Select type"
              testId="select-facility-type"
            />
            <InlineSelectField
              value={facility.facilityStatus || "indicative"}
              options={STATUS_OPTIONS}
              onSave={(value) => handleFieldUpdate("facilityStatus", value)}
              label="Status"
              placeholder="Select status"
              testId="select-facility-status"
            />
            <InlineSelectField
              value={facility.lenderId || "__none__"}
              options={lenderOptions}
              onSave={(value) => handleFieldUpdate("lenderId", value === "__none__" ? null : value)}
              label="Lender"
              placeholder="Select lender"
              testId="select-facility-lender"
            />
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Parent Deal</p>
              <p className="text-sm">{deal?.dealName || "-"}</p>
            </div>
            {facility.parentFacilityId && (
              <div data-testid="facility-overview-parent">
                <p className="text-xs font-medium text-muted-foreground mb-1">Parent Facility</p>
                {parentFacility ? (
                  <Link href={`/facilities/${facility.parentFacilityId}`} className="text-sm text-primary hover:underline">
                    {parentFacility.facilityName}
                  </Link>
                ) : (
                  <p className="text-sm text-muted-foreground">Loading…</p>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">AMOUNTS</h3>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Requested Amount</p>
              <p className="text-sm font-medium">{formatCurrency(facility.requestedAmount)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Approved Limit</p>
              <p className="text-sm font-medium">{formatCurrency(facility.approvedLimit)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Settled Amount</p>
              <p className="text-sm font-medium text-green-600">{formatCurrency(facility.fundedAmount)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-key-dates">
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">KEY DATES</h3>
          <div className="grid grid-cols-2 gap-3">
            <InlineDateField
              value={facility.applicationDate || ""}
              onSave={(value) => handleFieldUpdate("applicationDate", value || null)}
              label="Application Date"
              placeholder="Pick a date"
              testId="input-application-date"
            />
            <InlineDateField
              value={facility.approvalDate || ""}
              onSave={(value) => handleFieldUpdate("approvalDate", value || null)}
              label="Approval Date"
              placeholder="Pick a date"
              testId="input-approval-date"
            />
            <div data-testid="facility-overview-finance-clause-date">
              <InlineDateField
                value={facility.financeClauseDate || ""}
                onSave={(value) => handleFieldUpdate("financeClauseDate", value || null)}
                label="Finance Clause Date"
                placeholder="Pick a date"
                testId="input-finance-clause-date"
              />
            </div>
            <div data-testid="facility-overview-expected-settlement-date">
              <InlineDateField
                value={facility.expectedSettlementDate || ""}
                onSave={(value) => handleFieldUpdate("expectedSettlementDate", value || null)}
                label="Expected Settlement Date"
                placeholder="Pick a date"
                testId="input-expected-settlement-date"
              />
            </div>
            <div data-testid="facility-overview-actual-settlement-date">
              <InlineDateField
                value={facility.actualSettlementDate || ""}
                onSave={(value) => handleFieldUpdate("actualSettlementDate", value || null)}
                label="Actual Settlement Date"
                placeholder="Pick a date"
                testId="input-actual-settlement-date"
              />
            </div>
            <InlineDateField
              value={facility.maturityDate || ""}
              onSave={(value) => handleFieldUpdate("maturityDate", value || null)}
              label="Expiry Date"
              placeholder="Pick a date"
              testId="input-maturity-date"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-3">
          <h3 className="text-xs font-semibold text-muted-foreground mb-2">METADATA</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Created</p>
              <p className="text-sm">{formatDate(facility.createdAt)}</p>
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Last Updated</p>
              <p className="text-sm">{formatDate(facility.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ISSUE-119 Rework #1: Settled Confirmation Dialog */}
      <AlertDialog open={showSettledConfirm} onOpenChange={(open) => {
        if (!open) handleSettledCancel();
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Settled Amount</AlertDialogTitle>
            <AlertDialogDescription>
              The facility status is being changed to "Settled". Please confirm the Settled Amount.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label htmlFor="settledAmountInput">
              Settled Amount <span className="text-destructive">*</span>
            </Label>
            <Input
              id="settledAmountInput"
              type="number"
              step="0.01"
              value={settledAmountInput}
              onChange={(e) => setSettledAmountInput(e.target.value)}
              placeholder="0.00"
              className="mt-2"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Pre-filled with Approved Limit. Edit if needed.
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleSettledCancel}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleSettledConfirm}>
              Confirm Amount
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
